import type { RxDocument } from 'rxdb';
import { useAtom } from 'jotai';
import { FirebaseError } from 'firebase/app';

import { AI_CREDITS_EXHAUSTED } from '@flintmint/core-shared/constants';

import { capture } from '@/analytics';
import { useServices } from '@/providers/services-provider';
import { useApiRequest } from '@/hooks/api';
import { pendingUserSourceResyncsAtom } from '@/state';
import { useOpenAiRefusalInvitation } from '@/features/billing/ai-refusal-invitation';

import type { UserSource } from '@flintmint/core-shared/rxdb';

type ResyncParams = {
  newUrl?: string;
};

export const useResyncUserSource = (userSource?: RxDocument<UserSource>, onError?: (error: Error) => void) => {
  const { callableApi } = useServices();
  const requestId = userSource?.id ?? 'default';
  const { isResyncPending, setPendingResync, clearPendingResync } = useUserSourceResyncPending(userSource?.id);
  const openAiRefusalInvitation = useOpenAiRefusalInvitation();

  const { mutate } = useApiRequest<ResyncParams>(
    requestId,
    async ({ newUrl }, _signal) => {
      if (!userSource) return;

      if (!callableApi) {
        throw new Error('Resync is unavailable until the connection to the server is established');
      }

      await callableApi.resyncUserSource(userSource.id, newUrl ? { url: newUrl } : undefined);
    },
    {
      onError: async (error) => {
        // An AI Energy refusal is not a resync failure: it opens the AI refusal invitation instead.
        if (isAiCreditsExhausted(error)) {
          capture('ai_credits_exhausted', { source: 'user_source_resync' });
          openAiRefusalInvitation();
          return;
        }

        console.error('resync failed', error);
        onError?.(error);
      },
    }
  );

  const isResyncing = userSource?.syncStatus === 'processing' || isResyncPending;

  const runResync = (newUrl?: string) => {
    if (!userSource || isResyncing) return Promise.resolve();

    setPendingResync();
    return mutate({ newUrl })
      .then(() => {
        capture('user_source_resynced', { has_new_url: !!newUrl });
      })
      .finally(() => {
        clearPendingResync();
      });
  };

  return {
    isResyncing,
    resync: () => runResync().catch(() => {}),
    resyncWithUrl: (newUrl: string) => runResync(newUrl),
  };
};

const useUserSourceResyncPending = (userSourceId?: string) => {
  const [pendingResyncs = {}, setPendingResyncs] = useAtom(pendingUserSourceResyncsAtom);

  return {
    isResyncPending: !!userSourceId && !!pendingResyncs[userSourceId],
    setPendingResync: () => {
      if (!userSourceId) return;

      setPendingResyncs((pendingResyncs) => ({
        ...pendingResyncs,
        [userSourceId]: true,
      }));
    },
    clearPendingResync: () => {
      if (!userSourceId) return;

      setPendingResyncs((pendingResyncs) => {
        const { [userSourceId]: _removed, ...nextPendingResyncs } = pendingResyncs;
        return nextPendingResyncs;
      });
    },
  };
};

// The Firebase functions SDK appends the HTTP status to the message (`ai_credits_exhausted [400]`).
const aiCreditsExhaustedMessage = new RegExp(`^${AI_CREDITS_EXHAUSTED}( \\[\\d+\\])?$`);

/** The `resyncUserSource` callable's refusal: `HttpsError('failed-precondition', 'ai_credits_exhausted')`. */
export const isAiCreditsExhausted = (error: unknown) =>
  error instanceof FirebaseError &&
  error.code === 'functions/failed-precondition' &&
  aiCreditsExhaustedMessage.test(error.message);
